<script lang="ts">
  /**
   * MonsterModeScreen - Main gameplay screen for Monster Mode
   * Player controls a monster and collects letters to spell a word
   */

  import { onMount } from 'svelte';
  import { gameState, isWordComplete } from '$lib/stores/gameState';
  import { gameEngine } from '$lib/systems/gameEngine';
  import { inputSystem } from '$lib/systems/inputSystem';
  import { collisionSystem } from '$lib/systems/collisionSystem';
  import { audioSystem } from '$lib/systems/audioSystem';

  import StarField from '../ui/StarField.svelte';
  import Monster from '../game/Monster.svelte';
  import ScoreDisplay from '../game/ScoreDisplay.svelte';
  import BackButton from '../game/BackButton.svelte';
  import WordBoxes from '../game/WordBoxes.svelte';
  import Letter from '../game/Letter.svelte';
  import ParticleSystem from '../effects/ParticleSystem.svelte';

  // Letter entity interface
  interface LetterEntity {
    id: string;
    letter: string;
    position: { x: number; y: number };
    collected: boolean;
    isCorrect: boolean;
  }

  // Props
  interface Props {
    onBack?: () => void;
    onComplete?: () => void;
  }

  let { onBack = () => {}, onComplete = () => {} }: Props = $props();

  // Game state
  let monsterX = $state(0);
  let monsterY = $state(0);
  let showEatAnimation = $state(false);
  let isMoving = $state(false);
  let letters = $state<LetterEntity[]>([]);
  let nextLetterId = 0;
  let completionTriggered = $state(false);

  // Directional sound state
  let lastDirection = $state<'horizontal' | 'vertical' | null>(null);
  let lastSoundTime = $state(0);

  // Particle system reference
  let particleSystem: any;

  // Constants
  const MONSTER_SPEED = 480; // pixels per second (same as Letter Mode)
  const POINTS_PER_LETTER = 10;
  const COMPLETION_BONUS = 50;
  const DISTRACTOR_COUNT = 4; // Number of incorrect letters to spawn
  const SOUND_DEBOUNCE_MS = 2000; // Debounce time for directional sounds (2 seconds minimum spacing)
  const MIN_LETTER_SPACING = 90; // Minimum distance between letter centers (Letter radius 35px * 2 + 20px margin)

  // Watch for word completion
  $effect(() => {
    if ($isWordComplete && $gameState.targetWord && !completionTriggered) {
      handleWordCompletion();
    }
  });

  // Handle word completion
  function handleWordCompletion() {
    if (!$gameState.targetWord || completionTriggered) return;

    // Mark as triggered to prevent multiple calls
    completionTriggered = true;

    // Stop game loop to prevent further updates
    gameEngine.stopGameLoop();
    inputSystem.stop();

    // Award completion bonus
    gameState.addScore(COMPLETION_BONUS);

    // Pronounce the complete word
    audioSystem.pronounceWord($gameState.targetWord.word);

    // Trigger celebration after a short delay
    setTimeout(() => {
      onComplete();
    }, 2000);
  }

  // Initialize monster position
  onMount(() => {
    monsterX = window.innerWidth / 2;
    monsterY = window.innerHeight / 2;

    // Spawn letters
    spawnLetters();

    // Start systems
    inputSystem.start();
    gameEngine.startGameLoop(gameLoop);

    // Cleanup
    return () => {
      // Only stop if not already stopped by completion
      if (!completionTriggered) {
        inputSystem.stop();
        gameEngine.stopGameLoop();
      }
    };
  });

  // Main game loop
  function gameLoop(deltaTime: number) {
    updateMonsterMovement(deltaTime);
    checkLetterCollisions();
  }

  // Detect direction changes and play appropriate sounds for NEXT letter
  function detectDirectionChange(input: { x: number; y: number }) {
    const now = performance.now();

    // Debounce check - prevent rapid-fire sounds
    if (now - lastSoundTime < SOUND_DEBOUNCE_MS) {
      return;
    }

    // Get next target letter using sequenceIndex
    const targetWord = $gameState.targetWord;
    if (!targetWord) return;

    const sequenceIndex = $gameState.sequenceIndex;
    if (sequenceIndex >= targetWord.boxes.length) return; // Word complete

    const nextLetter = targetWord.boxes[sequenceIndex].letter;

    // Determine primary direction (horizontal vs vertical)
    const isHorizontal = Math.abs(input.x) > Math.abs(input.y);
    const currentDirection = isHorizontal ? 'horizontal' : 'vertical';

    // Only play sound on direction change (not continuously)
    if (currentDirection !== lastDirection && (input.x !== 0 || input.y !== 0)) {
      lastDirection = currentDirection;
      lastSoundTime = now;

      if (isHorizontal) {
        // Left/Right movement → play next letter's PHONEME (immediate mode - drop if busy)
        audioSystem.playPhoneme(nextLetter, 'short', true);
      } else {
        // Up/Down movement → play next letter's NAME (immediate mode - drop if busy)
        audioSystem.playLetterName(nextLetter, true);
      }
    }

    // Reset direction when no input
    if (input.x === 0 && input.y === 0) {
      lastDirection = null;
    }
  }

  // Update monster position based on input
  function updateMonsterMovement(deltaTime: number) {
    if (!inputSystem.hasInput()) {
      isMoving = false;
      lastDirection = null; // Reset direction when no input
      return;
    }

    isMoving = true;
    const input = inputSystem.getInputVector();

    // Detect direction changes and play directional sounds
    detectDirectionChange(input);

    const distance = MONSTER_SPEED * deltaTime;

    monsterX += input.x * distance;
    monsterY += input.y * distance;

    // Clamp to screen boundaries
    const margin = 50;
    monsterX = Math.max(margin, Math.min(window.innerWidth - margin, monsterX));
    monsterY = Math.max(margin, Math.min(window.innerHeight - margin, monsterY));
  }

  // Spawn all letters for the target word plus distractors
  function spawnLetters() {
    if (!$gameState.targetWord) return;

    const width = window.innerWidth;
    const height = window.innerHeight;
    const headerHeight = 140; // Account for header area
    const safeMargin = 100; // Margin from edges and center

    const spawnedLetters: LetterEntity[] = [];

    // Spawn one of each letter from the target word
    $gameState.targetWord.word.split('').forEach((letter, index) => {
      const position = getRandomPosition(width, height, headerHeight, safeMargin, spawnedLetters);
      spawnedLetters.push({
        id: `letter-${nextLetterId++}`,
        letter: letter.toLowerCase(),
        position,
        collected: false,
        isCorrect: true
      });
    });

    // Spawn distractor letters (not in target word)
    const targetLetters = new Set($gameState.targetWord.word.toLowerCase().split(''));
    const allLetters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const distractorPool = allLetters.split('').filter(l => !targetLetters.has(l.toLowerCase()));

    for (let i = 0; i < DISTRACTOR_COUNT; i++) {
      const randomLetter = distractorPool[Math.floor(Math.random() * distractorPool.length)];
      const position = getRandomPosition(width, height, headerHeight, safeMargin, spawnedLetters);
      spawnedLetters.push({
        id: `letter-${nextLetterId++}`,
        letter: randomLetter.toLowerCase(),
        position,
        collected: false,
        isCorrect: false
      });
    }

    letters = spawnedLetters;
  }

  // Get random position avoiding center, edges, and other letters
  function getRandomPosition(
    width: number,
    height: number,
    headerHeight: number,
    margin: number,
    existingLetters: LetterEntity[] = []
  ): { x: number; y: number } {
    const centerX = width / 2;
    const centerY = height / 2;
    const minDistFromCenter = 150; // Don't spawn too close to monster start

    let x, y, attempts = 0;
    const maxAttempts = 50; // Increased for collision checking

    do {
      x = margin + Math.random() * (width - margin * 2);
      y = headerHeight + margin + Math.random() * (height - headerHeight - margin * 2);

      // Check distance from center (monster start position)
      const distFromCenter = Math.sqrt((x - centerX) ** 2 + (y - centerY) ** 2);
      if (distFromCenter <= minDistFromCenter) {
        attempts++;
        continue;
      }

      // Check distance from all existing letters
      let tooClose = false;
      for (const letter of existingLetters) {
        if (letter.collected) continue; // Skip collected letters

        const dx = x - letter.position.x;
        const dy = y - letter.position.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        if (distance < MIN_LETTER_SPACING) {
          tooClose = true;
          break;
        }
      }

      if (!tooClose) {
        break; // Found a good position
      }

      attempts++;
    } while (attempts < maxAttempts);

    return { x, y };
  }

  // Check for collisions between monster and letters
  function checkLetterCollisions() {
    letters.forEach((letter, index) => {
      if (letter.collected) return;

      // Calculate distance between monster center and letter center
      const dx = monsterX - letter.position.x;
      const dy = monsterY - letter.position.y;
      const distance = Math.sqrt(dx * dx + dy * dy);

      // Collision detection using ACTUAL component sizes:
      // Monster: 100px width (radius = 50px)
      // Letter: 70px width (radius = 35px)
      // Total = 85px, but require slight overlap for better feel = 80px
      const collisionDistance = 80;

      if (distance < collisionDistance) {
        if (letter.isCorrect) {
          handleCorrectLetter(letter, index);
        } else {
          handleIncorrectLetter(letter, index);
        }
      }
    });
  }

  // Handle correct letter collection
  function handleCorrectLetter(letter: LetterEntity, index: number) {
    if (!$gameState.targetWord) return;

    // Find the first unfilled box for this letter
    const boxIndex = $gameState.targetWord.boxes.findIndex(
      box => box.letter === letter.letter && !box.filled
    );

    if (boxIndex === -1) return; // All boxes for this letter already filled

    // Mark letter as collected
    letters[index].collected = true;

    // Fill the word box
    gameState.fillWordBox(boxIndex, letter.letter);

    // Award points
    gameState.addScore(POINTS_PER_LETTER);

    // Play letter sound
    audioSystem.playLetterSound(letter.letter);

    // Particle burst at letter position
    if (particleSystem) {
      particleSystem.createBurst(letter.position.x, letter.position.y);
    }

    // Show eat animation
    showEatAnimation = true;
    setTimeout(() => {
      showEatAnimation = false;
    }, 300);
  }

  // Handle incorrect letter collection
  function handleIncorrectLetter(letter: LetterEntity, index: number) {
    // Play letter sound (educational - teach the letter)
    audioSystem.playLetterSound(letter.letter);

    // Bounce letter to new position (Easy mode - no penalties)
    const width = window.innerWidth;
    const height = window.innerHeight;
    letters[index].position = getRandomPosition(width, height, 140, 100, letters);
  }
</script>

<div class="monster-mode-screen">
  <StarField />

  <!-- UI Header -->
  <div class="header">
    <div class="header-left">
      <BackButton onclick={onBack} />
    </div>

    <div class="header-center">
      <WordBoxes targetWord={$gameState.targetWord} />
    </div>

    <div class="header-right">
      <ScoreDisplay score={$gameState.score} streak={$gameState.streak} />
    </div>
  </div>

  <!-- Game Area -->
  <div class="game-area">
    <!-- Monster Character -->
    <Monster
      x={monsterX}
      y={monsterY}
      showEat={showEatAnimation}
      isMoving={isMoving}
      debug={true}
    />

    <!-- Letter Entities -->
    {#each letters as letter (letter.id)}
      <Letter
        letter={letter.letter}
        x={letter.position.x}
        y={letter.position.y}
        collected={letter.collected}
        debug={true}
      />
    {/each}
  </div>

  <!-- Particle System -->
  <ParticleSystem bind:this={particleSystem} />
</div>

<style>
  .monster-mode-screen {
    position: relative;
    width: 100vw;
    height: 100vh;
    overflow: hidden;
  }

  .header {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 120px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: var(--space-md);
    background: rgba(10, 22, 40, 0.85);
    backdrop-filter: blur(4px);
    z-index: 100;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  }

  .header-left {
    display: flex;
    align-items: center;
  }

  .header-center {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .header-right {
    display: flex;
    align-items: center;
  }

  .game-area {
    position: absolute;
    inset: 0;
    z-index: 1;
  }

  /* Responsive */
  @media (max-width: 767px) {
    .header {
      height: 100px;
      padding: var(--space-sm);
    }
  }
</style>
